import { limitesDoDia, limitesDoMes, limitesEmMilissegundos, } from '../tempo/limitesDoMes.js';
/**
 * Até quando uma sessão **aberta** conta: no máximo até a meia-noite do dia
 * em que ela começou.
 *
 * Por que existe um teto, e por que é este. Uma sessão que ninguém pausou
 * não tem fim, e sem teto ela cresce para sempre: o cartão "No mês" mostrava
 * centenas de horas, o mostrador do cronômetro mostrava 30 h e o cartão
 * "Hoje" mostrava **zero** — porque o recorte do dia é pelo `inicio`, e o
 * dia da sessão esquecida já passou. Três números da mesma tela
 * contradizendo uns aos outros, e o do mês entrando no relatório que o
 * usuário entrega.
 *
 * A meia-noite do próprio dia é o teto óbvio porque ninguém fica no campo
 * depois dela sem perceber. Fechar a sessão sozinho seria escrever no banco
 * do usuário sem que ele tivesse pedido; parar de contar diz a mesma verdade
 * sem inventar dado. A sessão continua aberta e o cronômetro diz que ela
 * ficou aberta desde outro dia.
 *
 * **O teto é da sessão aberta, e acaba quando ela fecha.** Quem pausar amanhã
 * grava o `fim` de amanhã, e a partir daí a sessão conta inteira, de `inicio`
 * a `fim`, sem teto nenhum — `duracaoMs` só consulta o limite no ramo
 * `fim === null`. É deliberado: um `fim` é um fato que o usuário registrou, e
 * recortá-lo seria o app decidir que ele não estava lá; o teto existe
 * enquanto não há fim nenhum em que acreditar. Uma sessão esquecida por
 * trinta horas, depois de pausada, mantém as trinta horas registradas, mas
 * cada relatório recebe apenas a parcela sobreposta ao seu período.
 */
function limiteDaSessaoAberta(inicioMs) {
    return limitesEmMilissegundos(limitesDoDia(new Date(inicioMs))).fim;
}
/**
 * Esta sessão está aberta desde um dia que já acabou? É o que a tela Hoje
 * mostra em vez de "Em andamento": o mostrador parou, e sem dizer por quê
 * ele pareceria quebrado.
 */
export function sessaoEsquecida(sessao, agora = new Date()) {
    if (sessao.fim !== null)
        return false;
    const inicio = new Date(sessao.inicio).getTime();
    if (!Number.isFinite(inicio))
        return false;
    return agora.getTime() >= limiteDaSessaoAberta(inicio);
}
/**
 * A duração crua, em milissegundos. É esta que se soma.
 *
 * Truncar por sessão e só depois somar perde até 59 s **em cada sessão**, e
 * o app fecha uma sessão a cada pausa e a cada troca de categoria: uma manhã
 * com seis paradas pode sumir com cinco minutos do mês. Quem trunca é o
 * total, uma vez só.
 *
 * Sessão aberta conta até `agora` **ou até a meia-noite do dia em que
 * começou**, o que vier primeiro (ver `limiteDaSessaoAberta`). O teto vale
 * aqui, e não em cada chamador, para que o mostrador do cronômetro, os
 * cartões de Hoje e o relatório do mês digam todos o mesmo número.
 */
export function duracaoMs(sessao, agora = new Date()) {
    const inicio = new Date(sessao.inicio).getTime();
    // Data ilegível não é duração nenhuma. Sem esta guarda o teto abaixo
    // chamaria `toISOString()` sobre uma data inválida, que **lança**.
    if (!Number.isFinite(inicio))
        return 0;
    const fim = sessao.fim === null
        ? Math.min(agora.getTime(), limiteDaSessaoAberta(inicio))
        : new Date(sessao.fim).getTime();
    return Math.max(0, fim - inicio);
}
/** Parcela da duração da sessão que se sobrepõe ao período. */
export function duracaoNoPeriodoMs(sessao, limites, agora = new Date()) {
    const inicio = new Date(sessao.inicio).getTime();
    const duracao = duracaoMs(sessao, agora);
    const { inicio: de, fim: ate } = limitesEmMilissegundos(limites);
    if (sessao.deleted || !Number.isFinite(inicio + duracao))
        return 0;
    return Math.max(0, Math.min(inicio + duracao, ate) - Math.max(inicio, de));
}
/** A duração de **uma** sessão em minutos inteiros. Serve para mostrar o
 *  cronômetro e para perguntar "chegou a um minuto?"; para somar, use
 *  `duracaoMs` e trunque o total. */
export function duracaoMinutos(sessao, agora = new Date()) {
    return Math.floor(duracaoMs(sessao, agora) / 60_000);
}
/** Soma em milissegundos e trunca uma vez, no fim. */
export function minutosDaSoma(milissegundos) {
    return Math.floor(milissegundos / 60_000);
}
export function minutosDoMes(sessoes, ano, mes, agora = new Date()) {
    const limites = limitesDoMes(ano, mes);
    let campoMs = 0;
    let creditosMs = 0;
    for (const s of sessoes) {
        const ms = duracaoNoPeriodoMs(s, limites, agora);
        if (s.categoria === 'credito')
            creditosMs += ms;
        else
            campoMs += ms;
    }
    // Um truncamento por total, não um por sessão: os segundos soltos de cada
    // parada se juntam e só o que sobrar do minuto do mês se perde.
    return { campo: minutosDaSoma(campoMs), creditos: minutosDaSoma(creditosMs) };
}
// O relatório pede horas inteiras; o que sobra passa para o mês seguinte.
export function fecharHoras(minutosCampo, transporteAnterior) {
    const total = minutosCampo + transporteAnterior;
    return { horas: Math.floor(total / 60), transporte: total % 60 };
}
//# sourceMappingURL=horas.js.map